import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Label,
} from "reactstrap";
import { Catalogue } from "../../../types/catalogue.types";
import notifications from "../../../common/Components/Notifications/Notification";
import {
  CATALOGUE_PRICE_PRESETS,
  PRICE_SAFETY_PCT,
  buildPricingUpsertEntries,
  changePctVsDefault,
  effectiveClientTier,
  fetchCataloguePriceQuote,
  fetchCataloguePriceQuotes,
  fetchCataloguePricingSheet,
  groupQuotesByClient,
  inputsFromSheet,
  parseRowEffective,
  saveCataloguePricing,
  type CataloguePriceQuoteDetail,
  type CataloguePriceQuoteSummary,
  type CataloguePriceTier,
  type PricingSheetEntry,
} from "../cataloguePricing";

interface PricingTabProps {
  catalogue: Catalogue;
  priceTier: CataloguePriceTier;
  onPriceTierChange: (next: CataloguePriceTier) => void;
}

const HKP_PRIMARY = "#0071E3";
const HKP_BORDER = "#0071E3";
/** Readable on light and dark parent themes */
const PANEL_BG = "#f8f9fa";
const PANEL_TEXT = "#212529";
const PANEL_BORDER = "#ced4da";
const CODE_BG = "#e9ecef";

const formatQuoteWhen = (iso?: string): string => {
  if (!iso) return "Unknown date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const PricingDiary: React.FC<{
  catalogueId?: string;
  active: boolean;
  refreshTick: number;
}> = ({
  catalogueId,
  active,
  refreshTick,
}) => {
  const [quotes, setQuotes] = useState<CataloguePriceQuoteSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clientKey, setClientKey] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CataloguePriceQuoteDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadQuotes = useCallback(async () => {
    if (!catalogueId) return;
    setLoading(true);
    setError("");
    try {
      const rows = await fetchCataloguePriceQuotes(catalogueId);
      setQuotes(rows);
    } catch (err) {
      setQuotes([]);
      setError(err instanceof Error ? err.message : "Could not load price diary.");
    } finally {
      setLoading(false);
    }
  }, [catalogueId]);

  useEffect(() => {
    setClientKey(null);
    setQuoteId(null);
    setDetail(null);
    if (active) {
      loadQuotes();
    }
  }, [active, catalogueId, loadQuotes, refreshTick]);

  useEffect(() => {
    if (!active || !catalogueId || !quoteId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    fetchCataloguePriceQuote(catalogueId, quoteId)
      .then((row) => {
        if (!cancelled) setDetail(row);
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail(null);
          notifications({
            title: "Diary",
            message: err instanceof Error ? err.message : "Could not load quote.",
            type: "danger",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, catalogueId, quoteId]);

  const groups = useMemo(() => groupQuotesByClient(quotes), [quotes]);
  const selectedGroup = groups.find(
    (g) => g.clientName.trim().toLowerCase() === clientKey
  );
  const selectedQuote = selectedGroup?.quotes.find((q) => q.id === quoteId);

  if (!active) {
    return null;
  }

  if (loading) {
    return <p className="small text-muted mb-0">Loading diary…</p>;
  }
  if (error) {
    return (
      <div>
        <p className="small text-danger mb-2">{error}</p>
        <Button type="button" size="sm" outline onClick={() => loadQuotes()}>
          Retry
        </Button>
      </div>
    );
  }
  if (quotes.length === 0) {
    return (
      <p className="small mb-0" style={{ color: "#495057" }}>
        Name the client when you Share. Each send is remembered here with the prices they saw.
      </p>
    );
  }

  return (
    <div className="catalogue-price-diary">
      {clientKey && (
        <button
          type="button"
          className="catalogue-price-diary__back"
          onClick={() => {
            if (quoteId) {
              setQuoteId(null);
              setDetail(null);
              return;
            }
            setClientKey(null);
          }}
        >
          Back
        </button>
      )}
      {!clientKey && (
        <div className="catalogue-price-diary__list">
          {groups.map((group) => (
            <button
              type="button"
              key={group.clientName}
              className="catalogue-price-diary__item"
              onClick={() => setClientKey(group.clientName.trim().toLowerCase())}
            >
              <strong>{group.clientName}</strong>
              <span>
                {group.quotes.length} quote{group.quotes.length === 1 ? "" : "s"}
              </span>
            </button>
          ))}
        </div>
      )}
      {clientKey && selectedGroup && !quoteId && (
        <div className="catalogue-price-diary__list">
          <p className="catalogue-price-diary__heading">{selectedGroup.clientName}</p>
          {selectedGroup.quotes.map((quote) => (
            <button
              type="button"
              key={quote.id}
              className="catalogue-price-diary__item"
              onClick={() => setQuoteId(quote.id)}
            >
              <strong>{formatQuoteWhen(quote.createdAt)}</strong>
              <span>
                {quote.clientTier || "DEFAULT"} · {quote.entryCount || 0} product
                {quote.entryCount === 1 ? "" : "s"}
              </span>
            </button>
          ))}
        </div>
      )}
      {quoteId && (
        <div>
          <p className="catalogue-price-diary__heading">
            {selectedGroup?.clientName || detail?.clientName || "Quote"}
          </p>
          <p className="small text-muted">
            {formatQuoteWhen(selectedQuote?.createdAt || detail?.createdAt)} ·{" "}
            {selectedQuote?.clientTier || detail?.clientTier || "DEFAULT"}
          </p>
          {detailLoading ? (
            <p className="small text-muted mb-0">Loading products…</p>
          ) : (
            <ul className="catalogue-price-diary__lines">
              {(detail?.entries || []).map((line) => (
                <li key={line.productUniqueId}>
                  <span>{line.productName}</span>
                  <strong>₹{Number(line.price || 0).toLocaleString("en-IN")}</strong>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const escapeCsvCell = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const PricingTab: React.FC<PricingTabProps> = ({
  catalogue,
  priceTier,
  onPriceTierChange,
}) => {
  const [entries, setEntries] = useState<PricingSheetEntry[]>([]);
  /** Raw input per product: empty string = use product default */
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Bulk % (e.g. 10 = +10%, -5 = 5% off). Applied to row inputs only until Save. */
  const [globalMarkupPct, setGlobalMarkupPct] = useState<string>("");
  /** When true, bulk % uses each row’s current effective price; when false, product record default. */
  const [bulkFromCurrentEffective, setBulkFromCurrentEffective] = useState<boolean>(false);
  const [pane, setPane] = useState<"set" | "diary">("set");
  const [diaryTick, setDiaryTick] = useState(0);

  const effectiveTier = effectiveClientTier(priceTier);

  const loadSheet = useCallback(async () => {
    if (!catalogue?.id) return;
    setLoading(true);
    try {
      const sheet = await fetchCataloguePricingSheet(catalogue.id, effectiveTier);
      setEntries(sheet.entries);
      setPriceInputs(inputsFromSheet(sheet.entries));
    } catch (error) {
      notifications({
        title: "Pricing",
        message: error instanceof Error ? error.message : "Could not load catalogue pricing.",
        type: "danger",
      });
      setEntries([]);
      setPriceInputs({});
    } finally {
      setLoading(false);
    }
  }, [catalogue?.id, effectiveTier]);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  const parseEffectiveForRow = (row: PricingSheetEntry): number =>
    parseRowEffective(row, priceInputs);

  const applyGlobalMarkupToAllRows = () => {
    const pct = parseFloat(globalMarkupPct);
    if (Number.isNaN(pct)) {
      notifications({
        title: "Bulk markup",
        message: "Enter a valid percentage (e.g. 10 or -15).",
        type: "warning",
      });
      return;
    }
    if (entries.length === 0) return;
    const next: Record<string, string> = { ...priceInputs };
    let applied = 0;
    for (const row of entries) {
      const base = bulkFromCurrentEffective
        ? parseEffectiveForRow(row)
        : row.productDefaultPrice;
      if (base <= 0) {
        continue;
      }
      const v = Math.round(base * (1 + pct / 100));
      next[row.productUniqueId] = String(Math.max(0, v));
      applied++;
    }
    setPriceInputs(next);
    notifications({
      title: "Bulk markup applied",
      message: `${applied} row(s) updated (${bulkFromCurrentEffective ? "from current row price" : "from product default"}) × (1 + ${pct}%). Click Save prices to persist.`,
      type: "success",
    });
  };

  const previewRows = useMemo(() => {
    return entries.map((row) => {
      const oldPrice = row.productDefaultPrice;
      const newPrice = parseRowEffective(row, priceInputs);
      const diff = newPrice - oldPrice;
      const diffPct = changePctVsDefault(oldPrice, newPrice);
      return { ...row, oldPrice, newPrice, diff, diffPct };
    });
  }, [entries, priceInputs]);

  const riskyRows = useMemo(
    () => previewRows.filter((r) => Math.abs(r.diffPct) >= PRICE_SAFETY_PCT),
    [previewRows]
  );

  const previewSummary = useMemo(() => {
    if (previewRows.length === 0) return null;
    let sumDefault = 0;
    let sumEffective = 0;
    for (const row of previewRows) {
      sumDefault += row.oldPrice;
      sumEffective += row.newPrice;
    }
    const delta = sumEffective - sumDefault;
    return { sumDefault, sumEffective, delta, count: previewRows.length };
  }, [previewRows]);

  const handleSave = async () => {
    if (!catalogue?.id) return;

    let payloadEntries;
    try {
      payloadEntries = buildPricingUpsertEntries(entries, priceInputs);
    } catch (error) {
      notifications({
        title: "Pricing",
        message: error instanceof Error ? error.message : "Invalid price.",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      const savedTier = await saveCataloguePricing(catalogue.id, effectiveTier, payloadEntries);
      notifications({
        title: "Saved",
        message: `Catalogue prices updated for tier “${savedTier}”.`,
        type: "success",
      });
      await loadSheet();
    } catch (error) {
      notifications({
        title: "Pricing",
        message: error instanceof Error ? error.message : "Could not save pricing.",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (entries.length === 0) {
      notifications({
        title: "Export",
        message: "No rows to export.",
        type: "info",
      });
      return;
    }
    const header = [
      "clientTier",
      "productUniqueId",
      "productName",
      "oldPriceInr",
      "catalogueOverrideInr",
      "changeInr",
      "changePct",
      "newPriceInr",
    ];
    const rows = previewRows.map((row) => {
      const raw = (priceInputs[row.productUniqueId] ?? "").trim();
      const override = raw === "" ? "" : raw;
      return [
        effectiveTier,
        row.productUniqueId,
        row.name,
        String(row.oldPrice),
        override,
        String(row.diff),
        row.diffPct.toFixed(2),
        String(row.newPrice),
      ];
    });
    const lines = [
      header.map(escapeCsvCell).join(","),
      ...rows.map((r) => r.map(escapeCsvCell).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute(
      "download",
      `hkp-catalogue-pricing-${catalogue.id}-${effectiveTier}-${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notifications({
      title: "Export ready",
      message: "Pricing sheet downloaded.",
      type: "success",
    });
  };

  return (
    <div>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <h3 className="catalogue-tab-heading mb-0">Pricing tools</h3>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="catalogue-pricing-pane-toggle" role="tablist" aria-label="Pricing pane">
            <button
              type="button"
              role="tab"
              aria-selected={pane === "set"}
              className={pane === "set" ? "is-active" : ""}
              onClick={() => setPane("set")}
            >
              Set prices
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={pane === "diary"}
              className={pane === "diary" ? "is-active" : ""}
              onClick={() => setPane("diary")}
            >
              Diary
            </button>
          </div>
          <Button
            type="button"
            outline
            style={{ borderColor: HKP_BORDER, color: HKP_PRIMARY, fontSize: "14px" }}
            onClick={() => (pane === "diary" ? setDiaryTick((n) => n + 1) : loadSheet())}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {pane === "diary" ? (
        <PricingDiary catalogueId={catalogue?.id} active refreshTick={diaryTick} />
      ) : (
      <>

      {/* Explicit light panel so copy stays readable on dark global themes */}
      <div
        className="small mb-4 rounded border px-3 py-3"
        style={{
          backgroundColor: "#ffffff",
          color: PANEL_TEXT,
          borderColor: PANEL_BORDER,
          boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        }}
      >
        <p className="mb-0" style={{ color: PANEL_TEXT, lineHeight: 1.55 }}>
          <strong style={{ color: PANEL_TEXT }}>Tools for this catalogue and audience.</strong> Set
          individual prices next to product images on the Products tab. Defaults come from each product
          record. Overrides apply when viewers open the shared catalogue with the matching{" "}
          <code
            style={{
              backgroundColor: CODE_BG,
              color: "#111",
              padding: "2px 6px",
              borderRadius: 4,
              fontSize: "0.9em",
            }}
          >
            priceTier
          </code>{" "}
          query (e.g.{" "}
          <code
            style={{
              backgroundColor: CODE_BG,
              color: "#111",
              padding: "2px 6px",
              borderRadius: 4,
              fontSize: "0.9em",
            }}
          >
            ?priceTier=EXPORT
          </code>
          ). Tier <strong style={{ color: PANEL_TEXT }}>DEFAULT</strong> is used when no tier is specified.
        </p>
      </div>

      <Card className="mb-4" style={{ backgroundColor: "#fff" }}>
        <CardHeader
          className="small fw-semibold"
          style={{ backgroundColor: PANEL_BG, borderBottom: `1px solid ${PANEL_BORDER}`, color: PANEL_TEXT }}
        >
          Client tier (share audience)
        </CardHeader>
        <CardBody style={{ backgroundColor: "#fff", color: PANEL_TEXT }}>
          <Row className="g-3 align-items-end">
            <Col xs={12} md={4}>
              <Label className="small fw-bold mb-2 d-block" style={{ color: PANEL_TEXT }}>
                Preset
              </Label>
              <Input
                type="select"
                className="w-100"
                value={priceTier.mode === "preset" ? priceTier.preset : ""}
                disabled={priceTier.mode !== "preset"}
                onChange={(e) => {
                  onPriceTierChange({ ...priceTier, mode: "preset", preset: e.target.value });
                }}
                style={{ color: "#212529", backgroundColor: "#fff" }}
              >
                {CATALOGUE_PRICE_PRESETS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Input>
            </Col>
            <Col xs={12} md={4}>
              <Label className="small fw-bold mb-2 d-block" style={{ color: PANEL_TEXT }}>
                Custom tier key
              </Label>
              <Input
                type="text"
                className="w-100"
                placeholder="e.g. VIP_WHOLESALE"
                value={priceTier.custom}
                onChange={(e) => {
                  onPriceTierChange({ ...priceTier, mode: "custom", custom: e.target.value });
                }}
                style={{ color: "#212529", backgroundColor: "#fff" }}
              />
            </Col>
            <Col xs={12} md={4}>
              <Label className="small fw-bold mb-2 d-block" style={{ color: PANEL_TEXT }}>
                Active tier
              </Label>
              <div
                className="d-flex align-items-center px-2 rounded border w-100"
                style={{
                  minHeight: "calc(1.5em + 0.75rem + 2px)",
                  backgroundColor: PANEL_BG,
                  borderColor: PANEL_BORDER,
                  color: PANEL_TEXT,
                  fontWeight: 600,
                }}
              >
                {effectiveTier}
              </div>
            </Col>
          </Row>
          <p className="small mt-3 mb-0" style={{ color: "#495057" }}>
            Custom key: letters, digits, dot, underscore, hyphen; max 48 characters. Choose preset or type a
            custom key (typing switches to custom).
          </p>
        </CardBody>
      </Card>

      <Card className="mb-4" style={{ backgroundColor: "#fff" }}>
        <CardHeader
          className="small fw-semibold"
          style={{ backgroundColor: PANEL_BG, borderBottom: `1px solid ${PANEL_BORDER}`, color: PANEL_TEXT }}
        >
          Bulk markup (all products)
        </CardHeader>
        <CardBody style={{ backgroundColor: "#fff", color: PANEL_TEXT }}>
          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md={3}>
              <Label className="small fw-bold mb-2 d-block" style={{ color: PANEL_TEXT }}>
                Adjust all by %
              </Label>
              <Input
                type="number"
                step="0.1"
                placeholder="e.g. 10 or -15"
                value={globalMarkupPct}
                onChange={(e) => setGlobalMarkupPct(e.target.value)}
                disabled={loading || entries.length === 0}
                style={{ color: "#212529", backgroundColor: "#fff" }}
              />
            </Col>
            <Col xs={12} sm={6} md={5} className="d-flex align-items-center">
              <Label check className="small mb-0" style={{ color: PANEL_TEXT }}>
                <Input
                  type="checkbox"
                  className="me-2"
                  checked={bulkFromCurrentEffective}
                  onChange={(e) => setBulkFromCurrentEffective(e.target.checked)}
                  disabled={loading || entries.length === 0}
                />
                Apply % to <strong>current</strong> row prices (effective). Off = use each product’s
                default from record.
              </Label>
            </Col>
            <Col xs={12} md={4} className="d-flex">
              <Button
                type="button"
                className="align-self-end w-100 w-md-auto"
                style={{ backgroundColor: HKP_PRIMARY, borderColor: HKP_BORDER, color: "#fff" }}
                onClick={applyGlobalMarkupToAllRows}
                disabled={loading || entries.length === 0}
              >
                Apply to all rows
              </Button>
            </Col>
          </Row>
          {previewSummary && entries.length > 0 && (
            <div
              className="mt-3 small rounded border px-3 py-2"
              style={{ backgroundColor: PANEL_BG, borderColor: PANEL_BORDER, color: PANEL_TEXT }}
            >
              Preview totals ({previewSummary.count} products): default ₹
              {previewSummary.sumDefault.toLocaleString("en-IN")} → effective ₹
              {previewSummary.sumEffective.toLocaleString("en-IN")}
              {previewSummary.delta !== 0 && (
                <span className={previewSummary.delta >= 0 ? "text-success" : "text-danger"}>
                  {" "}
                  ({previewSummary.delta >= 0 ? "+" : ""}
                  ₹{previewSummary.delta.toLocaleString("en-IN")})
                </span>
              )}
              . Save to persist this tier.
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="mb-4" style={{ backgroundColor: "#fff" }}>
        <CardHeader
          className="small fw-semibold"
          style={{ backgroundColor: PANEL_BG, borderBottom: `1px solid ${PANEL_BORDER}`, color: PANEL_TEXT }}
        >
          Review before save
        </CardHeader>
        <CardBody style={{ backgroundColor: "#fff", color: PANEL_TEXT }}>
          {loading ? (
            <p className="small text-muted mb-0">Loading pricing…</p>
          ) : entries.length === 0 ? (
            <p className="small text-muted mb-0">
              No products in this catalogue. Add pieces on the Products tab first.
            </p>
          ) : (
            <p className="small mb-0" style={{ color: "#495057" }}>
              {entries.length} product{entries.length === 1 ? "" : "s"} on tier{" "}
              <strong>{effectiveTier}</strong>. Open the Products tab to set a price while looking at
              the image. Use bulk % above, then Save prices here.
            </p>
          )}
        </CardBody>
      </Card>

      {previewRows.length > 0 && (
        <Card className="mb-4" style={{ backgroundColor: "#fff" }}>
          <CardHeader
            className="small fw-semibold"
            style={{
              backgroundColor: riskyRows.length > 0 ? "#fff8e6" : PANEL_BG,
              borderBottom: `1px solid ${PANEL_BORDER}`,
              color: PANEL_TEXT,
            }}
          >
            Safety check
          </CardHeader>
          <CardBody style={{ backgroundColor: "#fff", color: PANEL_TEXT }}>
            {riskyRows.length === 0 ? (
              <p className="small mb-0" style={{ color: "#495057" }}>
                All adjustments are within ±{PRICE_SAFETY_PCT}% of each product’s default price.
              </p>
            ) : (
              <div>
                <p className="small mb-2" style={{ color: "#b45309", fontWeight: 600 }}>
                  {riskyRows.length} item{riskyRows.length === 1 ? "" : "s"} change by{" "}
                  {PRICE_SAFETY_PCT}% or more versus product default. Confirm before saving.
                </p>
                <ul className="small mb-0" style={{ color: PANEL_TEXT }}>
                  {riskyRows.map((r) => (
                    <li key={r.productUniqueId}>
                      {r.name} — {r.diffPct >= 0 ? "+" : ""}
                      {r.diffPct.toFixed(1)}% (₹{r.oldPrice.toLocaleString("en-IN")} → ₹
                      {r.newPrice.toLocaleString("en-IN")})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <div className="d-flex flex-column flex-sm-row gap-2 justify-content-sm-end">
        <Button
          type="button"
          outline
          style={{ borderColor: HKP_BORDER, color: HKP_PRIMARY, fontSize: "14px" }}
          onClick={handleExport}
          disabled={loading || entries.length === 0}
        >
          Export CSV
        </Button>
        <Button
          type="button"
          style={{
            backgroundColor: HKP_PRIMARY,
            borderColor: HKP_BORDER,
            fontSize: "14px",
          }}
          onClick={handleSave}
          disabled={loading || saving || entries.length === 0}
        >
          {saving ? "Saving…" : "Save prices"}
        </Button>
      </div>
      </>
      )}
    </div>
  );
};

export default PricingTab;
